﻿/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class HeaderLocationSelector extends HTMLElement {
  connectedCallback() {
    this._button = this.querySelector('[data-action="open-store-picker"]');
    this._nameEl = this.querySelector('[data-location-name]');
    this._stores = [];

    try {
      this._stores = JSON.parse(this.dataset.stores || '[]');
    } catch (error) {
      this._stores = [];
    }

    this._defaultLabel = this.dataset.defaultLabel || 'Select a store';

    if (this._button) {
      this._button.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('age-gate:open-stores'));
      });
    }

    this.updateLabel();
    document.addEventListener('age-gate:store-selected', () => this.updateLabel());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateLabel();
      }
    });
  }

  _readLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return raw;
    }
  }

  updateLabel() {
    if (!this._nameEl) return;

    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));
    this._nameEl.textContent = store?.name || this._defaultLabel;
  }
}

if (!window.customElements.get('header-location-selector')) {
  window.customElements.define('header-location-selector', HeaderLocationSelector);
}

class StoreInfoSection extends HTMLElement {
  connectedCallback() {
    this._headingEl = this.querySelector('[data-store-heading]');
    this._addressEl = this.querySelector('[data-store-address]');
    this._hoursEl = this.querySelector('[data-store-hours]');
    this._detailsEl = this.querySelector('.store-info__details');
    this._contactEl = this.querySelector('[data-store-contact]');
    this._phoneEl = this.querySelector('[data-store-phone]');
    this._phoneLinkEl = this.querySelector('[data-store-phone-link]');
    this._imageEl = this.querySelector('.store-info__image:not(.store-info__image--placeholder)');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._headingPrefix = this.dataset.headingPrefix || 'Shopping at';
    this._fallback = {
      heading: this.dataset.fallbackHeading || '',
      address: this.dataset.fallbackAddress || '',
      hours: this.dataset.fallbackHours || '',
      phone: this.dataset.fallbackPhone || '',
      image: this.dataset.fallbackImage || '',
    };

    this.querySelectorAll('[data-action="open-store-picker"]').forEach((button) => {
      button.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('age-gate:open-stores'));
      });
    });

    this.updateContent();
    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
  }

  _readLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return raw;
    }
  }

  _formatAddress(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(', ');
    const line2 = [store.city, store.provinceCode || store.province, store.zip]
      .filter(Boolean)
      .join(', ');
    return [line1, line2].filter(Boolean).join(', ');
  }

  _formatHours(store) {
    if (!store) return '';

    if (store.storeUntil) {
      const until = String(store.storeUntil).trim();
      if (until && !/^until$/i.test(until)) return until;
    }

    if (store.storeHours) {
      const hours = String(store.storeHours).trim();
      if (hours && hours !== '[object Object]') return hours;
    }

    return '';
  }

  _setText(el, value, { allowBreaks = false } = {}) {
    if (!el) return;

    const text = value || '';
    if (allowBreaks) {
      el.replaceChildren();
      text.split('\n').forEach((line, index) => {
        if (index > 0) el.appendChild(document.createElement('br'));
        el.appendChild(document.createTextNode(line));
      });
      return;
    }

    el.textContent = text;
  }

  updateContent() {
    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));

    const heading = store
      ? [this._headingPrefix, store.name].filter(Boolean).join(' ').trim()
      : this._fallback.heading;
    const address = store ? this._formatAddress(store) : this._fallback.address;
    const hours = store ? this._formatHours(store) || this._fallback.hours : this._fallback.hours;
    // Selected store: only show its own phone (no fallback). No store: use section fallback.
    const phone = store
      ? String(store.phone || '').trim()
      : String(this._fallback.phone || '').trim();
    const image = store ? store.image || this._fallback.image : this._fallback.image;

    this._setText(this._headingEl, heading);
    this._setText(this._addressEl, address, { allowBreaks: true });
    this._setText(this._hoursEl, hours);
    this._setText(this._phoneEl, phone);

    if (this._contactEl) {
      this._contactEl.hidden = !phone;
    }

    if (this._detailsEl) {
      const hoursVisible = Boolean(this._hoursEl && String(hours || '').trim());
      const contactVisible = Boolean(phone);
      this._detailsEl.hidden = !hoursVisible && !contactVisible;
    }

    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phone
        ? `tel:${String(phone).replace(/[\s()-]/g, '')}`
        : '#';
    }

    if (this._imageEl && image) {
      this._imageEl.setAttribute('src', image);
      if (this._imageEl.hasAttribute('srcset')) {
        this._imageEl.removeAttribute('srcset');
      }
      this._imageEl.setAttribute('alt', heading || '');
    }
  }
}

if (!window.customElements.get('store-info-section')) {
  window.customElements.define('store-info-section', StoreInfoSection);
}

class MegaMenuTabs extends HTMLElement {
  connectedCallback() {
    this._tabs = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this._panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    if (!this._tabs.length || !this._panels.length) {
      return;
    }

    this._onTabClick = (event) => {
      const trigger = event.target.closest('[data-tab-trigger]');
      if (!trigger || !this.contains(trigger)) {
        return;
      }

      event.preventDefault();
      this.activateTab(trigger.dataset.tabTrigger);
    };

    this._onKeyDown = (event) => {
      const currentIndex = this._tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (currentIndex === -1) {
        return;
      }

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this._tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this._tabs.length) % this._tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this._tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this._tabs[nextIndex].dataset.tabTrigger, true);
    };

    this._onDropdownOpen = () => {
      if (!this.closest('.mega-menu:not([hidden])')) {
        return;
      }

      const firstTab = this._tabs[0];
      if (firstTab) {
        this.activateTab(firstTab.dataset.tabTrigger);
      }
    };

    this._onBlockSelect = (event) => {
      const trigger = event.target.closest('[data-tab-trigger]');
      const panel = event.target.closest('[data-tab-panel]');

      if (trigger) {
        this.activateTab(trigger.dataset.tabTrigger, true);
      } else if (panel) {
        this.activateTab(panel.dataset.tabPanel, true);
      }
    };

    this.addEventListener('click', this._onTabClick);
    this.addEventListener('keydown', this._onKeyDown);
    this.addEventListener('shopify:block:select', this._onBlockSelect);

    const desktopNavigation = this.closest('desktop-navigation');
    if (desktopNavigation) {
      desktopNavigation.addEventListener('desktop-nav:dropdown:open', this._onDropdownOpen);
      this._desktopNavigation = desktopNavigation;
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onTabClick);
    this.removeEventListener('keydown', this._onKeyDown);
    this.removeEventListener('shopify:block:select', this._onBlockSelect);

    if (this._desktopNavigation) {
      this._desktopNavigation.removeEventListener('desktop-nav:dropdown:open', this._onDropdownOpen);
    }
  }

  activateTab(tabId, focusTab = false) {
    if (!tabId) {
      return;
    }

    this._tabs.forEach((tab) => {
      const isActive = tab.dataset.tabTrigger === tabId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;

      if (isActive && focusTab) {
        tab.focus();
      }
    });

    this._panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === tabId;
      panel.toggleAttribute('hidden', !isActive);
    });
  }
}

if (!window.customElements.get('mega-menu-tabs')) {
  window.customElements.define('mega-menu-tabs', MegaMenuTabs);
}

function getThumbnailSlideshowAutoplayMs(slideshow) {
  const duration = getComputedStyle(slideshow).getPropertyValue('--section-autoplay-duration').trim();

  if (!duration || duration === '0s') {
    return 5000;
  }

  return parseFloat(duration) * 1000;
}

function lockThumbnailSlideshowHeight(slideshow, { remeasure = false } = {}) {
  const firstSlide = slideshow.querySelector('slide-show-item');

  if (!firstSlide) {
    return;
  }

  const width = slideshow.getBoundingClientRect().width;

  if (width < 1) {
    return;
  }

  let height = 0;

  if (remeasure || !slideshow.dataset.thumbnailHeightRatio) {
    if (!firstSlide.hasAttribute('hidden')) {
      slideshow.classList.remove('thumbnail-slideshow--locked-height');
      slideshow.style.removeProperty('--thumbnail-slideshow-locked-height');
      height = firstSlide.getBoundingClientRect().height;

      if (height > 0) {
        slideshow.dataset.thumbnailHeightRatio = String(height / width);
      }
    } else if (slideshow.dataset.thumbnailHeightRatio) {
      height = width * parseFloat(slideshow.dataset.thumbnailHeightRatio);
    }
  } else {
    height = width * parseFloat(slideshow.dataset.thumbnailHeightRatio);
  }

  if (!(height > 0)) {
    return;
  }

  const maxHeight = parseFloat(
    getComputedStyle(slideshow).getPropertyValue('--thumbnail-slideshow-max-height')
  );

  if (maxHeight > 0) {
    height = Math.min(height, maxHeight);
  }

  const nextHeight = `${Math.round(height)}px`;

  if (slideshow.style.getPropertyValue('--thumbnail-slideshow-locked-height') === nextHeight) {
    slideshow.classList.add('thumbnail-slideshow--locked-height');
    return;
  }

  slideshow.style.setProperty('--thumbnail-slideshow-locked-height', nextHeight);
  slideshow.classList.add('thumbnail-slideshow--locked-height');
}

function setupThumbnailSlideshowHeightLock(slideshow) {
  if (slideshow.dataset.thumbnailHeightLockInit) {
    return;
  }

  slideshow.dataset.thumbnailHeightLockInit = 'true';

  const sync = (options) => lockThumbnailSlideshowHeight(slideshow, options);
  const images = slideshow.querySelectorAll('slide-show-item:first-child img');

  sync({ remeasure: true });

  images.forEach((image) => {
    if (image.complete) {
      return;
    }

    image.addEventListener('load', () => sync({ remeasure: true }), { once: true });
  });

  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(() => sync());
    resizeObserver.observe(slideshow);
  } else {
    window.addEventListener('resize', () => sync());
  }
}

function setupThumbnailSlideshow(slideshow) {
  if (!slideshow?.classList?.contains('thumbnail-slideshow') || slideshow.dataset.thumbnailSlideshowInit) {
    return;
  }

  slideshow.dataset.thumbnailSlideshowInit = 'true';
  setupThumbnailSlideshowHeightLock(slideshow);

  slideshow.querySelector('[data-action="prev"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.previous();
    slideshow.startPlayer();
  });

  slideshow.querySelector('[data-action="next"]')?.addEventListener('click', (event) => {
    event.preventDefault();
    slideshow.pausePlayer();
    slideshow.next();
    slideshow.startPlayer();
  });

  slideshow.startPlayer = function thumbnailStartPlayer() {
    if (!this.hasAttribute('auto-play')) {
      return;
    }

    this.style.setProperty('--section-animation-play-state', 'running');
    clearInterval(this._thumbnailAutoplayTimer);
    this._thumbnailAutoplayTimer = setInterval(() => {
      if (this.style.getPropertyValue('--section-animation-play-state') !== 'running') {
        return;
      }

      this.next();
    }, getThumbnailSlideshowAutoplayMs(this));
  };

  slideshow.pausePlayer = function thumbnailPausePlayer() {
    this.style.setProperty('--section-animation-play-state', 'paused');
    clearInterval(this._thumbnailAutoplayTimer);
  };

  if (slideshow.hasAttribute('auto-play')) {
    slideshow.startPlayer();
  }
}

function initThumbnailSlideshows(root = document) {
  const boot = () => {
    root.querySelectorAll('slide-show.thumbnail-slideshow').forEach(setupThumbnailSlideshow);
  };

  if (window.customElements.get('slide-show')) {
    boot();
    return;
  }

  window.customElements.whenDefined('slide-show').then(boot);
}

document.addEventListener('DOMContentLoaded', () => initThumbnailSlideshows());
document.addEventListener('shopify:section:load', (event) => {
  initThumbnailSlideshows(event.target);
});

initThumbnailSlideshows();
/* ------------------------------------------------------------------ */
/* Featured deal - mobile dots / slide sync                           */
/* ------------------------------------------------------------------ */

function setupFeaturedDeal(carousel) {
  if (!carousel || carousel.dataset.featuredDealInit) {
    return;
  }

  const track = carousel.querySelector('.featured-deal__track');
  const pageDots = carousel.querySelector('.featured-deal__dots');
  const cards = Array.from(carousel.querySelectorAll('.featured-deal__card'));
  const mobileNav = carousel.querySelector('prev-next-buttons.featured-deal__mobile-arrows');
  const desktopNav = carousel.querySelector('prev-next-buttons.featured-deal__nav');

  if (!track || cards.length < 2) {
    return;
  }

  carousel.dataset.featuredDealInit = 'true';

  let isProgrammaticScroll = false;

  const getSelectedIndex = () => {
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const scrollLeft = track.scrollLeft * direction;
    const cardWidth = cards[0].offsetWidth + (parseInt(getComputedStyle(carousel).getPropertyValue('--multi-column-column-gap'), 10) || 0);
    if (cardWidth <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(cards.length - 1, Math.round(scrollLeft / cardWidth)));
  };

  const syncNav = (index) => {
    if (pageDots) {
      pageDots.selectedIndex = index;
    }

    if (mobileNav) {
      mobileNav.isPrevDisabled = index <= 0;
      mobileNav.isNextDisabled = index >= cards.length - 1;
    }

    if (desktopNav) {
      desktopNav.isPrevDisabled = index <= 0;
      desktopNav.isNextDisabled = index >= cards.length - 1;
    }
  };

  const select = (index, animate = true) => {
    const clampIndex = Math.max(0, Math.min(cards.length - 1, index));
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const columnGap = parseInt(getComputedStyle(carousel).getPropertyValue('--multi-column-column-gap'), 10) || 0;
    const left = direction * (cards[0].offsetWidth + columnGap) * clampIndex;

    isProgrammaticScroll = true;
    track.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
    syncNav(clampIndex);

    window.setTimeout(() => {
      isProgrammaticScroll = false;
    }, 400);
  };

  if (pageDots) {
    carousel.addEventListener('page-dots:changed', (event) => {
      select(event.detail.index, true);
    });
  }

  track.addEventListener('scroll', () => {
    if (isProgrammaticScroll) {
      return;
    }
    syncNav(getSelectedIndex());
  }, { passive: true });

  syncNav(0);
}

function initFeaturedDeals(root = document) {
  root.querySelectorAll('multi-column.featured-deal__carousel').forEach(setupFeaturedDeal);
}

document.addEventListener('DOMContentLoaded', () => initFeaturedDeals());
document.addEventListener('shopify:section:load', (event) => {
  initFeaturedDeals(event.target);
});

initFeaturedDeals();

/* ------------------------------------------------------------------ */
/* Featured collections (shop deals) - arrow visibility sync         */
/* ------------------------------------------------------------------ */

function setupFeaturedCollectionsArrows(productList) {
  if (!productList || productList.dataset.featuredCollectionsArrowsInit) {
    return;
  }

  const prevNext = productList.querySelector('.product-list__prev-next');
  const inner = productList.querySelector('.product-list__inner');
  const prevBtn = prevNext?.querySelector('.prev-next-button--prev');
  const nextBtn = prevNext?.querySelector('.prev-next-button--next');

  if (!prevNext || !inner || !prevBtn || !nextBtn) {
    return;
  }

  productList.dataset.featuredCollectionsArrowsInit = 'true';

  const syncArrows = () => {
    const direction = window.themeVariables?.settings?.direction === 'rtl' ? -1 : 1;
    const scrollLeft = inner.scrollLeft * direction;
    const maxScroll = inner.scrollWidth - inner.clientWidth;

    if (maxScroll <= 0) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    prevBtn.disabled = scrollLeft <= 1;
    nextBtn.disabled = scrollLeft >= maxScroll - 1;
  };

  inner.addEventListener('scroll', syncArrows, { passive: true });
  window.addEventListener('resize', syncArrows);
  productList.addEventListener('prev-next:prev', syncArrows);
  productList.addEventListener('prev-next:next', syncArrows);
  syncArrows();
}

function initFeaturedCollectionsArrows(root = document) {
  root.querySelectorAll('.shopify-section--featured-collections product-list').forEach(setupFeaturedCollectionsArrows);
}

document.addEventListener('DOMContentLoaded', () => initFeaturedCollectionsArrows());
document.addEventListener('shopify:section:load', (event) => {
  initFeaturedCollectionsArrows(event.target);
});

initFeaturedCollectionsArrows();

/* ------------------------------------------------------------------ */
/* Catalog facets - preserve province/location on Clear all           */
/* ------------------------------------------------------------------ */

function getPreservedFacetInputs(root = document) {
  return Array.from(
    root.querySelectorAll('#facet-filters-form input[type="hidden"][data-preserve-on-clear]')
  );
}

function withPreservedFacetParams(urlString) {
  const url = new URL(urlString, window.location.origin);
  const currentUrl = new URL(window.location.href);

  getPreservedFacetInputs().forEach((input) => {
    if (!input.name) {
      return;
    }

    const value = currentUrl.searchParams.get(input.name) || input.value;
    if (value) {
      url.searchParams.set(input.name, value);
    }
  });

  return url.toString();
}

function patchFacetClearFilters() {
  const FacetFilters = customElements.get('facet-filters')?.constructor;
  if (!FacetFilters || FacetFilters.prototype.__vbClearPatched) {
    return;
  }

  FacetFilters.prototype.__vbClearPatched = true;

  const originalClear = FacetFilters.prototype._onFiltersCleared;

  FacetFilters.prototype._onFiltersCleared = function onFiltersCleared(event, target) {
    const isClearAll =
      target.classList.contains('tag-link--catalog-clear') ||
      target.classList.contains('drawer__header-action--clear');

    if (isClearAll) {
      const baseHref = target.getAttribute('href');
      if (baseHref) {
        const nextUrl = withPreservedFacetParams(baseHref);
        target.setAttribute('href', nextUrl);
      }
    }

    return originalClear.call(this, event, target);
  };
}

if (customElements.get('facet-filters')) {
  patchFacetClearFilters();
} else {
  customElements.whenDefined('facet-filters').then(patchFacetClearFilters);
}

class LocationHeaderSection extends HTMLElement {
  connectedCallback() {
    this._headingEl = this.querySelector('[data-lh-heading]');
    this._addressEl = this.querySelector('[data-lh-address]');
    this._addressWrapEl = this.querySelector('[data-lh-address-wrap]');
    this._hoursEl = this.querySelector('[data-lh-hours]');
    this._hoursWrapEl = this.querySelector('[data-lh-hours-wrap]');
    this._detailsEl = this.querySelector('.location-header__details');
    this._contactEl = this.querySelector('[data-lh-contact]');
    this._phoneEl = this.querySelector('[data-lh-phone]');
    this._phoneLinkEl = this.querySelector('[data-lh-phone-link]');
    this._statusEl = this.querySelector('[data-lh-status]');
    this._untilEl = this.querySelector('[data-lh-until]');
    this._untilWrapEl = this.querySelector('[data-lh-until-wrap]');
    this._directionsEl = this.querySelector('[data-lh-directions]');
    this._imageEl = this.querySelector('.location-header__image:not(.location-header__image--placeholder)');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._labelOpen = this.dataset.labelOpen || 'Open';
    this._labelClosed = this.dataset.labelClosed || 'Closed';
    this._layoutStyle = this.dataset.layoutStyle || 'default';
    this._secondaryLink = this.dataset.secondaryLink || '';
    this._fallback = {
      heading: this.dataset.fallbackHeading || '',
      address: this.dataset.fallbackAddress || '',
      hours: this.dataset.fallbackHours || '',
      until: this.dataset.fallbackUntil || '',
      phone: this.dataset.fallbackPhone || '',
      image: this.dataset.fallbackImage || '',
    };

    this.updateContent();
    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
  }

  _readCurrentStoreLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return String(raw);
    }
  }

  _readLocationId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('location_id');
      if (fromUrl != null && String(fromUrl).trim() !== '') {
        return String(fromUrl).trim();
      }
    } catch (error) {
      /* ignore invalid URL */
    }

    return this._readCurrentStoreLocationId();
  }

  _formatAddress(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(' ');
    const line2 = [store.city, store.province || store.provinceCode].filter(Boolean).join(', ');
    return [line1, line2].filter(Boolean).join('  ');
  }

  _formatUntil(store) {
    if (!store || !store.storeUntil) return '';
    const until = String(store.storeUntil).trim();
    return until && !/^until$/i.test(until) ? until : '';
  }

  _formatHours(store) {
    if (!store || !store.storeHours) return '';
    const hours = String(store.storeHours).trim();
    if (!hours || hours === '[object Object]' || /^until\b/i.test(hours)) return '';
    return hours;
  }

  _parseClockToMinutes(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'object') {
      if (value.hour != null) {
        const hours = Number(value.hour);
        const minutes = Number(value.minute || 0);
        if (!Number.isNaN(hours)) return hours * 60 + minutes;
      }
      value = value.time || value.value || '';
    }
    const raw = String(value).trim();
    if (!raw || raw === '[object Object]') return null;
    const isoMatch = raw.match(/T(\d{1,2}):(\d{2})/);
    if (isoMatch) return Number(isoMatch[1]) * 60 + Number(isoMatch[2]);
    const ampm = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (ampm) {
      let hours = Number(ampm[1]) % 12;
      if (/pm/i.test(ampm[3])) hours += 12;
      return hours * 60 + Number(ampm[2]);
    }
    const twentyFour = raw.match(/^(\d{1,2}):(\d{2})/);
    if (twentyFour) return Number(twentyFour[1]) * 60 + Number(twentyFour[2]);
    return null;
  }

  _isOpen(store) {
    if (!store) return true;
    if (typeof store.available === 'boolean' && store.available === false) return false;
    const openMinutes = this._parseClockToMinutes(store.storeOpenTime);
    const closeMinutes = this._parseClockToMinutes(store.storeCloseTime);
    if (openMinutes == null && closeMinutes == null) return true;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (openMinutes != null && closeMinutes != null) {
      if (closeMinutes > openMinutes) return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
      return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
    }
    if (closeMinutes != null) return nowMinutes < closeMinutes;
    if (openMinutes != null) return nowMinutes >= openMinutes;
    return true;
  }

  _directionsUrl(store, address) {
    if (this._secondaryLink) return this._secondaryLink;
    if (store && store.latitude != null && store.longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
    }
    if (address) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    return '#';
  }

  _setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  updateContent() {
    let locationId = this._readLocationId();
    let store = this._stores.find((entry) => String(entry.id) === String(locationId));

    if (!store) {
      const currentId = this._readCurrentStoreLocationId();
      if (currentId && String(currentId) !== String(locationId)) {
        locationId = currentId;
        store = this._stores.find((entry) => String(entry.id) === String(locationId));
      }
    }

    const heading = store ? store.name : this._fallback.heading;
    const address = store ? this._formatAddress(store) : this._fallback.address;
    const hours = store ? this._formatHours(store) || this._fallback.hours : this._fallback.hours;
    const until = store ? this._formatUntil(store) || this._fallback.until : this._fallback.until;
    const phone = store ? String(store.phone || '').trim() : String(this._fallback.phone || '').trim();
    const image = store ? store.image || this._fallback.image : this._fallback.image;
    const isOpen = store ? this._isOpen(store) : true;

    this._setText(this._headingEl, heading);
    this._setText(this._addressEl, address);
    this._setText(this._hoursEl, hours);
    this._setText(this._phoneEl, phone);
    this._setText(this._untilEl, until);

    if (this._statusEl) {
      this._statusEl.textContent = isOpen ? this._labelOpen : this._labelClosed;
      this._statusEl.classList.toggle('is-closed', !isOpen);
    }
    if (this._untilWrapEl) this._untilWrapEl.hidden = !String(until || '').trim();
    if (this._hoursWrapEl) this._hoursWrapEl.hidden = !String(hours || '').trim();
    if (this._contactEl) this._contactEl.hidden = !phone;
    if (this._addressWrapEl) this._addressWrapEl.hidden = !String(address || '').trim();
    if (this._detailsEl) {
      const isStoreSection = this._layoutStyle === 'store_section';
      if (isStoreSection) {
        this._detailsEl.hidden = !String(address || '').trim() && !phone;
      } else {
        this._detailsEl.hidden = !String(hours || '').trim() && !phone;
      }
    }
    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phone ? `tel:${String(phone).replace(/[\s()-]/g, '')}` : '#';
    }
    if (this._directionsEl) {
      this._directionsEl.href = this._directionsUrl(store, address);
      if (!this._secondaryLink) {
        this._directionsEl.setAttribute('target', '_blank');
        this._directionsEl.setAttribute('rel', 'noopener');
      }
    }
    if (this._imageEl && image) {
      this._imageEl.setAttribute('src', image);
      if (this._imageEl.hasAttribute('srcset')) this._imageEl.removeAttribute('srcset');
      this._imageEl.setAttribute('alt', heading || '');
    }
  }
}

if (!window.customElements.get('location-header-section')) {
  window.customElements.define('location-header-section', LocationHeaderSection);
}

class LocationStoreContentSection extends HTMLElement {
  connectedCallback() {
    this._address1El = this.querySelector('[data-lsc-address1]');
    this._address2El = this.querySelector('[data-lsc-address2]');
    this._phoneEl = this.querySelector('[data-lsc-phone]');
    this._phoneLinkEl = this.querySelector('[data-lsc-phone-link]');
    this._phoneWrapEl = this.querySelector('[data-lsc-phone-wrap]');
    this._emailEl = this.querySelector('[data-lsc-email]');
    this._emailLinkEl = this.querySelector('[data-lsc-email-link]');
    this._emailWrapEl = this.querySelector('[data-lsc-email-wrap]');
    this._directionsEl = this.querySelector('[data-lsc-directions]');
    this._hoursListEl = this.querySelector('[data-lsc-hours-list]');
    this._servicesListEl = this.querySelector('[data-lsc-services]');
    this._stores = [];

    const storesSource = this.dataset.storesSource;
    if (storesSource) {
      const sourceEl = document.getElementById(storesSource);
      if (sourceEl) {
        try {
          this._stores = JSON.parse(sourceEl.textContent || '[]');
        } catch (error) {
          this._stores = [];
        }
      }
    }

    this._fallback = {
      address1: this.dataset.fallbackAddress1 || '',
      address2: this.dataset.fallbackAddress2 || '',
      phone: this.dataset.fallbackPhone || '',
      email: this.dataset.fallbackEmail || '',
    };

    this.updateContent();
    this._syncProductCarousel();

    document.addEventListener('age-gate:store-selected', () => this.updateContent());
    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this.updateContent();
      }
    });
    window.addEventListener('resize', () => this._syncProductCarousel());
  }

  _readLocationId() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('location_id');
      if (fromUrl != null && String(fromUrl).trim() !== '') {
        return String(fromUrl).trim();
      }
    } catch (error) {
      /* ignore */
    }

    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return String(raw);
    }
  }

  _setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
  }

  _formatPhoneHref(phone) {
    return String(phone || '').replace(/[\s()\-]/g, '');
  }

  _updateHours(hoursRows) {
    if (!this._hoursListEl || !Array.isArray(hoursRows)) return;

    hoursRows.forEach((row) => {
      const rowEl = this._hoursListEl.querySelector(`[data-lsc-hours-row][data-day="${row.label}"]`);
      if (!rowEl) return;
      const timeEl = rowEl.querySelector('[data-lsc-hours-time]');
      this._setText(timeEl, row.text);
      rowEl.classList.toggle('is-today', Boolean(row.isToday));
    });
  }

  _updateServices(services, hasStore) {
    if (!this._servicesListEl) return;

    const types = ['pickup', 'ageVerification', 'accessible', 'parking'];
    const anyDynamic = hasStore && services && types.some((type) => services[type]);

    types.forEach((type) => {
      const placeholderItem = this._servicesListEl.querySelector(
        `[data-lsc-service][data-service-type="${type}"][data-lsc-placeholder]`
      );
      const liveItem = this._servicesListEl.querySelector(
        `[data-lsc-service][data-service-type="${type}"]:not([data-lsc-placeholder])`
      );

      if (!hasStore || !anyDynamic) {
        if (placeholderItem) placeholderItem.hidden = false;
        if (liveItem) liveItem.hidden = true;
        return;
      }

      if (placeholderItem) placeholderItem.hidden = true;
      if (liveItem) liveItem.hidden = !services[type];
    });
  }

  _syncProductCarousel() {
    const carousel = this.querySelector('[data-lsc-products-carousel]');
    if (!carousel) return;

    const track = carousel.querySelector('[data-lsc-products-track]');
    const prevBtn = carousel.querySelector('[data-lsc-products-prev]');
    const nextBtn = carousel.querySelector('[data-lsc-products-next]');
    if (!track || !prevBtn || !nextBtn) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 4;
    carousel.classList.toggle('is-scrollable', scrollable);

    if (!scrollable) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      return;
    }

    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= maxScroll - 4;
    prevBtn.hidden = atStart;
    nextBtn.hidden = atEnd;
  }

  updateContent() {
    const locationId = this._readLocationId();
    const store = this._stores.find((entry) => String(entry.id) === String(locationId));

    const address1 = store?.address1 || this._fallback.address1;
    const address2 = store?.address2 || this._fallback.address2;
    const phone = store?.phone || this._fallback.phone;
    const email = (store?.email && String(store.email).trim()) ? store.email : this._fallback.email;

    if (this._address1El) {
      this._setText(this._address1El, address1);
      this._address1El.hidden = !String(address1 || '').trim();
    }
    if (this._address2El) {
      this._setText(this._address2El, address2);
      this._address2El.hidden = !String(address2 || '').trim();
    }

    const phoneVisible = Boolean(String(phone || '').trim());
    if (this._phoneEl) this._setText(this._phoneEl, phone);
    if (this._phoneLinkEl) {
      this._phoneLinkEl.href = phoneVisible ? `tel:${this._formatPhoneHref(phone)}` : '#';
    }
    if (this._phoneWrapEl) this._phoneWrapEl.hidden = !phoneVisible;

    const emailVisible = Boolean(String(email || '').trim());
    if (this._emailEl) this._setText(this._emailEl, email);
    if (this._emailLinkEl) {
      this._emailLinkEl.href = emailVisible ? `mailto:${email}` : '#';
    }
    if (this._emailWrapEl) this._emailWrapEl.hidden = !emailVisible;

    if (this._directionsEl && store) {
      if (store.latitude != null && store.longitude != null) {
        this._directionsEl.href = `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
      } else {
        const fullAddress = [address1, address2].filter(Boolean).join(', ');
        if (fullAddress) {
          this._directionsEl.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
        }
      }
    }

    if (store?.hoursRows) {
      this._updateHours(store.hoursRows);
    }

    this._updateServices(store?.services, Boolean(store));
  }
}

if (!window.customElements.get('location-store-content-section')) {
  window.customElements.define('location-store-content-section', LocationStoreContentSection);
}

/* Mini-cart + cart page store / fulfillment actions (survives cart-drawer AJAX refresh) */
document.addEventListener('click', (event) => {
  const nextBtn = event.target.closest('[data-mini-cart-categories-next], [data-vb-cart-popular-next], [data-lsc-products-next]');
  const prevBtn = event.target.closest('[data-mini-cart-categories-prev], [data-vb-cart-popular-prev], [data-lsc-products-prev]');
  if (nextBtn || prevBtn) {
    event.preventDefault();
    const carousel = event.target.closest('[data-mini-cart-categories-carousel], [data-vb-cart-popular-carousel], [data-lsc-products-carousel]');
    const track = carousel?.querySelector('[data-mini-cart-categories], [data-vb-cart-popular-track], [data-lsc-products-track]');
    if (!track) return;

    const card = track.querySelector('.mini-cart__category-card, .vb-cart__popular-card, .product-item, .lsc__products-card');
    const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.5;
    track.scrollBy({ left: nextBtn ? step : -step, behavior: 'smooth' });

    const lscSection = carousel?.closest('location-store-content-section');
    if (lscSection) {
      setTimeout(() => lscSection._syncProductCarousel?.(), 350);
    }
    return;
  }

  const root = event.target.closest('#mini-cart, .vb-cart');
  if (!root) return;

  const storeBtn = event.target.closest('[data-action="open-store-picker"]');
  if (storeBtn) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('age-gate:open-stores'));
    return;
  }

  const deliveryBtn = event.target.closest('[data-action="switch-to-delivery"]');
  if (deliveryBtn) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('product:switch-to-delivery'));
  }
});

document.addEventListener('scroll', (event) => {
  if (event.target?.matches?.('[data-lsc-products-track]')) {
    const section = event.target.closest('location-store-content-section');
    if (section) section._syncProductCarousel?.();
  }
}, true);

function syncMiniCartCategoryArrows(root = document.getElementById('mini-cart')) {
  if (!root) return;

  root.querySelectorAll('[data-mini-cart-categories-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-mini-cart-categories]');
    const prevBtn = carousel.querySelector('[data-mini-cart-categories-prev]');
    const nextBtn = carousel.querySelector('[data-mini-cart-categories-next]');
    if (!track || !prevBtn || !nextBtn) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 4;
    carousel.classList.toggle('is-scrollable', scrollable);

    if (!scrollable) {
      prevBtn.hidden = true;
      nextBtn.hidden = true;
      return;
    }

    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= maxScroll - 4;
    prevBtn.hidden = atStart;
    nextBtn.hidden = atEnd;
  });
}

document.addEventListener('scroll', (event) => {
  if (event.target?.matches?.('[data-mini-cart-categories]')) {
    syncMiniCartCategoryArrows(event.target.closest('#mini-cart'));
  }
}, true);

window.addEventListener('resize', () => syncMiniCartCategoryArrows());

const miniCartEl = document.getElementById('mini-cart');
if (miniCartEl) {
  syncMiniCartCategoryArrows(miniCartEl);

  const observer = new MutationObserver(() => syncMiniCartCategoryArrows(miniCartEl));
  observer.observe(miniCartEl, { childList: true, subtree: true });
}
